import React from 'react';
import { useParams, Outlet, Navigate } from 'react-router-dom';
import {
  useApiClient,
  useFetchImmutable,
  OtherChips,
  TripMatchChips,
  useFetch,
  BackButton,
} from '@marageti/z4-lib';
import {
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { TripMatch } from '@marageti/z4-sdk/lib/travel';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import { ReferralType } from '@marageti/z4-sdk/lib/travel/TripRequest';
import { RoleName } from '@marageti/z4-sdk/lib/auth';
import { formatCountryCodes } from '../../utils/helpers';
import { TabItem } from '../../components/sub-navigation/types';
import SubNavigation from '../../components/sub-navigation';
import { useAuthStore } from '../../store';

const NAV_LINKS: TabItem[] = [
  {
    label: 'acceptance',
    to: '',
  },
  {
    label: 'messages',
    to: 'messages',
  },
];

const { ChipRepeat, ChipPRL } = OtherChips;
const { getTripMatchStatusChip } = TripMatchChips;

const tagIcons = {
  RPT: <ChipRepeat />,
  PRL: <ChipPRL />,
};

const AcceptLeadSubNav = () => {
  const { loggedInAgent } = useAuthStore();
  const loggedInAgentIsAdmin = loggedInAgent?.userInfo.some((userInfo) => userInfo?.roles?.includes(RoleName.AGENT_ADMIN));
  const { tripMatchId } = useParams();
  const apiClient = useApiClient();
  const { data: countryCodes } = useFetchImmutable<CountryCodeMap>(apiClient.standardsClient.getCountryCodes, [], true);
  const { data: tripMatch } = useFetch<TripMatch>(apiClient.tripMatchesClient.getTripMatch, [tripMatchId], !!tripMatchId);

  const traveler = tripMatch?.travelers[0];
  const partnerName = tripMatch ? tripMatch.partnerName : <Skeleton width={150} />;
  const countries = tripMatch && countryCodes ? formatCountryCodes(tripMatch.countries, countryCodes) : <Skeleton width={100} />;
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('desktop'));

  const isPrlReferral = tripMatch && (
    tripMatch.referralType === ReferralType.OneTimePartnerReferral
    || tripMatch.referralType === ReferralType.LifetimePartnerReferral
  );

  const travelerNameElement = traveler?.fullName ? (
    <Typography
      component="span"
      variant="h3"
      fontWeight="bold"
      color="primary.main"
    >
      {traveler.fullName}
    </Typography>
  ) : <Skeleton width={250} />;

  const tripTagsRow = tripMatch ? (
    <Stack direction="row" spacing={0.75} alignItems="center">
      {tripMatch.status ? getTripMatchStatusChip(tripMatch.status, 'small') : null}
      {tripMatch.repeatTraveler ? tagIcons.RPT : null}
      {isPrlReferral ? tagIcons.PRL : null}
    </Stack>
  ) : null;

  const hasTripTags = !!tripMatch?.status || !!tripMatch?.repeatTraveler || !!isPrlReferral;

  if (!loggedInAgentIsAdmin) {
    return <Navigate to="/trip-matches" replace />;
  }
  return (
    <>
      <SubNavigation
        profile={{
          primary: isDesktop ? (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              {travelerNameElement}
              {tripTagsRow}
            </Stack>
          ) : travelerNameElement,
          secondary: countries,
          tertiary: partnerName,
          ...(!isDesktop && hasTripTags && tripTagsRow ? { decoration: tripTagsRow } : {}),
        }}
        tabs={NAV_LINKS}
        startAdornment={(
          <BackButton
            aria-label="back to trip matches"
            defaultPath="/trip-matches"
            edge="start"
          />
        )}
      />
      <Outlet />
    </>
  );
};

export default AcceptLeadSubNav;
